import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { object, string, mixed } from "yup";
import { useMutation, useQuery } from "@apollo/client";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Attribute, Game, GameKind, QuickRankMode } from "../../gql/graphql";
import { GAME_ALL } from "../../graphql/queries/GameQueries";
import { GAME_CREATE, QUICKRANK_CONFIGURATION_UPDATE } from "../../graphql/mutations/GameMutation";
import { getValidationErrors } from "../../utils/validation/getValidationErrors";
import { useProject } from "../project/ProjectContext";

interface GameValidationErrors {
  name?: string;
  kind?: string;
}

interface CreateGameData {
  name: string;
  kind: GameKind;
}

interface GameContextProps {
  errors: GameValidationErrors;
  currentGame: Game | null;
  currentAttributes: Attribute[];
  games: Game[];
  setCurrentGame: (game: Game) => void;
  settings: {
    swiperQuestion: string | undefined;
    setSwiperQuestion: (question: string | undefined) => void;
    isSwiperShuffled: boolean;
    setIsSwiperShuffled: (isShuffled: boolean) => void;
  };
  addGame: (newGame: CreateGameData, isQuickPick?: boolean) => Promise<Game | undefined>;
}

const GameContext = createContext<GameContextProps | undefined>(undefined);

export const GameProvider = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation("project");
  const { project: { id: projectId } = {} } = useProject();
  const [searchParams, setSearchParams] = useSearchParams();
  const [swiperQuestion, setSwiperQuestion] = useState<string | undefined>(undefined);
  const [isSwiperShuffled, setIsSwiperShuffled] = useState<boolean>(true);
  const [errors, setErrors] = useState<GameValidationErrors>({});
  const currentGameId = searchParams.get("gameId");

  const validationSchema = object({
    name: string().required(t("addGames.add.nameError") as string),
    kind: mixed<GameKind>()
      .oneOf(Object.values(GameKind))
      .required(t("addGames.add.kindError") as string),
  });

  const { data: { gameAll: games } = { gameAll: [] }, refetch: refetchGames } = useQuery(GAME_ALL, {
    variables: { projectId },
    skip: !projectId,
  });

  const currentGame = useMemo(() => {
    return games?.find((game: Game) => game.id === currentGameId) || null;
  }, [games, currentGameId]);

  const [createGame] = useMutation(GAME_CREATE);
  const [updateQuickRankConfiguration] = useMutation(QUICKRANK_CONFIGURATION_UPDATE);

  const setCurrentGame = (game: Game) => {
    searchParams.set("gameId", game.id);
    setSearchParams(searchParams);
  };

  const addGame = async (data: CreateGameData, isQuickPick = false): Promise<Game | undefined> => {
    if (!projectId) {
      throw new Error("Cannot add game without project");
    }

    const validationErrors = await getValidationErrors<GameValidationErrors>(data, validationSchema);
    setErrors(validationErrors ?? {});

    if (validationErrors) {
      return;
    }

    const { data: { gameCreate } = {} } = await createGame({
      variables: {
        data: {
          projectId,
          ...data,
        },
      },
      onCompleted: isQuickPick ? undefined : () => refetchGames(),
    });

    if (isQuickPick) {
      await updateQuickRankConfiguration({
        variables: {
          quickRankConfigurationId: gameCreate.configuration.id,
          data: {
            mode: QuickRankMode.AllUp,
          },
        },
        onCompleted: () => refetchGames(),
      });
    }

    return gameCreate;
  };

  const sortedGames = useMemo(
    () => [...games]?.sort((a: Game, b: Game) => (a.createdAt < b.createdAt ? -1 : 1)),
    [games],
  );

  useEffect(() => {
    setSwiperQuestion(currentGame?.configuration.question);
    setIsSwiperShuffled(currentGame?.configuration.shuffleCards || false);
  }, [currentGame]);

  return (
    <GameContext.Provider
      value={{
        currentGame,
        currentAttributes: currentGame?.configuration?.attributes || [],
        games: sortedGames,
        setCurrentGame,
        addGame,
        errors,
        settings: {
          swiperQuestion,
          setSwiperQuestion,
          isSwiperShuffled,
          setIsSwiperShuffled,
        },
      }}
    >
      {children}
    </GameContext.Provider>
  );
};

export const useGame = () => {
  const context = useContext(GameContext);

  if (context === undefined) {
    throw new Error("useGame must be used within a GameProvider");
  }

  return context;
};
